import I18n from 'i18n-js/index.js.erb';
import ToggleVisible from '../application/app/toggle_visible';

const t = (key, opts) => I18n.t(key, {
  scope: 'datatables',
  ...opts
});

const getRowId = (rowData) => {
  if (rowData.attributes) {
    return rowData.attributes.id;
  }
  return $(rowData[0]).data('id');
};

const renderHiddenRowIds = ($wrapper, selectedData) => {
  const idsFieldName = $wrapper.data('elementName');
  const selectedIds = selectedData.toArray().map(getRowId);
  const hiddenInputs = selectedIds.map(id => `<input type='hidden' name='${idsFieldName}[]' value='${id}'>`);
  $wrapper.html(hiddenInputs.join(''));
};

export const updateCheckAllCheckbox = (table, $checkAllCheckbox) => {
  const selectedRowsFromCurrentPage = table
    .rows({ selected: true, search: 'applied', page: 'current' })
    .data().length;
  const rowsFromCurrentPage = table.rows({ search: 'applied', page: 'current' }).data().length;
  $checkAllCheckbox.prop(
    'checked',
    rowsFromCurrentPage > 0 && selectedRowsFromCurrentPage === rowsFromCurrentPage
  );
};

export const evaluateSelectedRows = (table, $checkAllCheckbox, renderFlash = true) => {
  updateCheckAllCheckbox(table, $checkAllCheckbox);
  const selectedRowsFromAllPages = table.rows({ selected: true }).data().length;
  const selectedRowsData = table.rows({ selected: true }).data();
  const $hiddenSelectedWrapper = $checkAllCheckbox
    .closest('.js-action-bar-flash-wrapper')
    .find('.js-selected-rows-ids');
  renderHiddenRowIds($hiddenSelectedWrapper, selectedRowsData);
  if (!renderFlash) {
    return;
  }
  const $flash = $checkAllCheckbox.closest('.js-action-bar-flash-wrapper').find('.flash');
  if (selectedRowsFromAllPages > 0) {
    const pluralizedCounter =
      selectedRowsFromAllPages === 1 ? '1 Item' : `${selectedRowsFromAllPages} Items`;
    $flash.find('.js-items-selected-counter').text(`${pluralizedCounter} Selected`);
    $flash.trigger('selected-items:changed', [selectedRowsFromAllPages]);
    ToggleVisible.show($flash);
  } else {
    $flash.find('.js-items-selected-counter').text('');
    $flash.trigger('selected-items:changed', [0]);
    ToggleVisible.hide($flash);
  }
};

const setupSelectAllButton = (table) => {
  const $checkAllCheckbox = $('#checkall');
  $checkAllCheckbox.on('change', (event) => {
    const checkbox = event.target;
    if (checkbox.checked) {
      table.rows({ search: 'applied', page: 'current' }).select();
    } else {
      table.rows().deselect();
    }
  });
};

const setupUnselectAllButton = (table) => {
  $('#uncheckall').on('click', () => {
    table.rows().deselect();
  });
};

const setupSelectHooks = (table) => {
  const $checkAllCheckbox = $('#checkall');
  table.on('deselect', () => evaluateSelectedRows(table, $checkAllCheckbox));
  table.on('select', () => evaluateSelectedRows(table, $checkAllCheckbox));
};

const setupSearchInput = (table) => {
  let debounce = null;
  let drawnInput = null;
  const timeout = 350;
  table.on('destroy.interval', () => clearTimeout(debounce));
  $('.js-search-input').on('keyup change', (event) => {
    clearTimeout(debounce);
    debounce = setTimeout(() => {
      const value = $(event.target).val();
      if (value !== drawnInput) {
        table.search(value).draw();
        drawnInput = value;
      }
    }, timeout);
  });
};

const setupPageLengthDropdown = (table) => {
  $('.js-page-length-dropdown a').on('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const value = $(event.currentTarget).data('pageLength');
    table.page.len(value).draw();
    const labelForCurrentPageLength = $(event.currentTarget).text();
    $('.js-current-page-length').text(labelForCurrentPageLength);
  });
};

const exportedContentFormat = {
  body(data, _row, _column, node) {
    return node.dataset.display || data;
  }
};

const exportOptions = {
  format: exportedContentFormat,
  modifier: {
    page: 'all',
    search: 'applied'
  },
  columns: ':not(.not-export):visible'
};

const escapeForPrint = value => $('<div>').text(value == null ? '' : value).html();

// Strip filesystem-unsafe characters so the value is safe as a suggested PDF
// filename (the browser derives the filename from document.title).
const sanitizeForFilename = value => (value || '')
  .replace(/[\\/:*?"<>|]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

// Escape a string for use inside a CSS content: "..." value (printed footer).
const escapeForCss = value => (value || '').replace(/[\\"]/g, '');

// Print stylesheet for the polished report (used app-wide). The report is
// identified once, by the title/subtitle in a <thead> heading row that repeats
// on every page (with reserved space) — the reliable cross-browser running
// header. The page chrome carries no title (avoiding a duplicate); the footer
// shows the printed timestamp (left) and page numbers (right) in matching small,
// muted type, via @page margin boxes (rendered by paged-media engines /
// print-to-PDF).
const reportPrintStyles = (landscape, printedAt, eventName) => `
  @page { size: ${landscape ? 'landscape' : 'portrait'}; margin: 0.55in 0.4in 0.7in; }
  @page {
    @bottom-left { content: "Printed ${printedAt}"; font-size: 9px; color: #829ab1; }
    @bottom-center { content: "${eventName}"; font-size: 9px; color: #829ab1; }
    @bottom-right { content: "Page " counter(page) " of " counter(pages); font-size: 9px; color: #829ab1; }
  }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { margin: 0; color: #1f2933; font-size: 10px;
         font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
  table { width: 100%; border-collapse: collapse; table-layout: auto; }
  thead { display: table-header-group; }
  thead .report-heading th { border: none; padding: 0 0 4px; text-align: left; }
  .report-title { font-size: 20px; font-weight: 700; color: #102a43; line-height: 1.15; }
  .report-subtitle { margin-top: 3px; font-size: 11px; font-weight: 400; color: #486581; }
  thead .report-columns th { text-align: left; padding: 6px 8px; font-size: 9px; font-weight: 700;
    text-transform: uppercase; letter-spacing: .04em; color: #486581; border-bottom: 1.5px solid #334e68; }
  tbody td { padding: 5px 8px; font-size: 10px; color: #1f2933; border-bottom: .5px solid #d9e2ec; vertical-align: top; }
  tr { page-break-inside: avoid; }
`;

// The polished "report" print button used everywhere: a bold title with an
// optional smaller subtitle beneath it, a header that repeats on each page, and
// a layout that uses the full page width (landscape for wide tables). Pass
// exportOptions to override the default column selector — e.g. a print-only
// table that is hidden on screen must NOT use the ":visible" selector.
export const buildReportPrintButton = ({
  docTitle, title, subtitle, printTitle, footerName, exportOptions: exportOptionsOverride
} = {}) => ({
  extend: 'print',
  className: 'js-print-button',
  title: '',
  exportOptions: exportOptionsOverride || exportOptions,
  customize(win) {
    const doc = win.document;
    const $body = $(doc.body);
    // Print-only tables (e.g. Orders) ship hidden on screen; unhide them so the
    // cloned print window renders the table rather than a blank page.
    $body.find('table').removeClass('is-hidden');
    $body.find('th, td').removeClass(
      'table__data table__data--tiny js-checkbox-row table__header table__header--tiny is-print-hidden'
    );
    // Suggested PDF filename (and browser header, if enabled). Defaults to
    // "docTitle - title" (e.g. "Event - Activity"), de-duped so tables where
    // docTitle falls back to title don't read "X - X". printTitle overrides it
    // outright when the heading title isn't the right filename (e.g. a question
    // panel whose heading is the question name but whose file is "Event - Activity").
    const filenameParts = [docTitle, title].filter(Boolean).filter((part, i, all) => all.indexOf(part) === i);
    doc.title = sanitizeForFilename(printTitle || filenameParts.join(' - ')) || 'Eventene Report';

    const $table = $body.find('table').first();
    const colCount = $table.find('thead tr').first().children().length || 1;
    const $thead = $table.find('thead');
    $thead.find('tr').addClass('report-columns');
    const subtitleHtml = subtitle
      ? `<div class="report-subtitle">${escapeForPrint(subtitle)}</div>`
      : '';
    $thead.prepend(
      `<tr class="report-heading"><th colspan="${colCount}">`
      + `<div class="report-title">${escapeForPrint(title)}</div>${subtitleHtml}`
      + '</th></tr>'
    );

    const now = new Date();
    const printedAt = escapeForCss(`${now.toLocaleDateString()} ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
    // Footer center carries a report name (default: the event) so it survives
    // even when the user disables the browser's own headers/footers. footerName
    // overrides it (e.g. "Event - Activity" on the question panels).
    const footer = escapeForCss(footerName || docTitle);
    $('<style>').text(reportPrintStyles(colCount > 8, printedAt, footer)).appendTo(doc.head);
  }
});

// CSV and Copy button definitions; Print is the polished report rendered by
// buildReportPrintButton (shared across the app via setupExportButtons).
const exportButtonConfigs = () => ({
  csv: {
    extend: 'csv',
    className: 'js-csv-button',
    charset: 'UTF-8',
    bom: true,
    exportOptions
  },
  copy: {
    extend: 'copyHtml5',
    className: 'js-copy-to-clipboard-button',
    title: null,
    exportOptions
  }
});

// Resolve the print title/subtitle for a table. A table may supply an explicit
// title + subtitle via data-print-title / data-print-subtitle (Track uses the
// event name + activity); otherwise it falls back to its existing data-title as
// a single title with no subtitle.
export const resolvePrintHeader = (table) => {
  const node = $(table.table().node());
  const title = node.data('printTitle') || $(table.header()[0]).data('title') || $('head title').text();
  return {
    // Browser print header (if the user leaves it enabled): the event name, or
    // the heading title when no separate doc title is supplied.
    docTitle: node.data('printDocTitle') || title,
    // On-page heading above the table: activity name + activity date/time.
    title,
    subtitle: node.data('printSubtitle')
  };
};

const setupExportButtons = (table) => {
  const configs = exportButtonConfigs();
  const tableButtons = new $.fn.dataTable.Buttons(table, {
    buttons: [buildReportPrintButton(resolvePrintHeader(table)), configs.csv, configs.copy]
  });

  $('.js-export-print').click((event) => {
    event.preventDefault();
    table.button('.js-print-button').trigger();
  });

  $('.js-export-csv').click((event) => {
    event.preventDefault();
    table.button('.js-csv-button').trigger();
  });

  $('.js-export-clipboard').click((event) => {
    event.preventDefault();
    table.button('.js-copy-to-clipboard-button').trigger();
  });

  return tableButtons;
};

// Export all rows matching the current filters/search under server-side paging.
// Only the visible page is in the DOM, so we fetch the full filtered set
// (length: -1) and run the export over an off-screen table that reuses the
// exact same column renderers — guaranteeing identical output to the live
// table, just across every matching row instead of one page.
export const exportAllMatchingRows = (sourceTable, { url, columns, type, print }) => {
  const params = { ...sourceTable.ajax.params(), start: 0, length: -1 };
  return $.getJSON(url, params).then((response) => {
    const $tmp = $('<table>')
      .css({ position: 'absolute', left: '-9999px', top: 0 })
      .appendTo('body');
    // Reuse the live table's header so the export carries the exact visible
    // column labels (standard, tracking, survey, assignment, custom fields, in
    // the same order). `columns` must include the checkbox column so it aligns
    // with the cloned header; the export's `:not(.not-export):visible` selector
    // then drops the checkbox and hidden columns just as on the live table.
    $tmp.append($(sourceTable.table().header()).closest('thead').clone());
    const tmpTable = $tmp.DataTable({
      data: response.data || [],
      columns,
      paging: false,
      searching: false,
      info: false,
      ordering: false
    });
    const buttonConfig = type === 'print'
      ? buildReportPrintButton(print || resolvePrintHeader(sourceTable))
      : exportButtonConfigs()[type];
    // eslint-disable-next-line no-new
    new $.fn.dataTable.Buttons(tmpTable, { buttons: [buttonConfig] });
    tmpTable.button(0).trigger();
    setTimeout(() => {
      tmpTable.destroy();
      $tmp.remove();
    }, 200);
  });
};

// Export an in-memory set of rows — e.g. a side-panel list that isn't a
// DataTable — through the shared report-print / CSV / copy buttons. Builds a
// throwaway off-screen table from the given column titles + row values and
// triggers the chosen button, so non-DataTable surfaces get the exact same
// export output (report layout, filename, footer) as Track / Individual.
//   type: 'print' | 'csv' | 'copy'
//   columns: ['Full Name', 'Email', ...]   (header labels)
//   rows: [['Ann Lee', 'ann@e.com', ...], ...]
//   header: { docTitle, title, subtitle }   (print report heading; ignored for csv/copy)
export const exportRows = ({ type, columns, rows, header = {} }) => {
  const $tmp = $('<table>')
    .css({ position: 'absolute', left: '-9999px', top: 0 })
    .appendTo('body');
  const $headRow = $('<tr>').appendTo($('<thead>').appendTo($tmp));
  columns.forEach(title => $('<th>').text(title).appendTo($headRow));
  const tmpTable = $tmp.DataTable({
    data: rows,
    columns: columns.map(() => ({})),
    paging: false,
    searching: false,
    info: false,
    ordering: false
  });
  const button = type === 'print'
    ? buildReportPrintButton(header)
    : exportButtonConfigs()[type];
  // eslint-disable-next-line no-new
  new $.fn.dataTable.Buttons(tmpTable, { buttons: [button] });
  tmpTable.button(0).trigger();
  setTimeout(() => {
    tmpTable.destroy();
    $tmp.remove();
  }, 200);
};

export const defaultTableOptions = {
  pagingType: 'full_numbers',
  language: {
    decimal: t('decimal'),
    emptyTable: t('empty_table'),
    info: t('info'),
    infoEmpty: t('info_empty'),
    infoFiltered: t('info_filtered'),
    infoPostFix: t('info_postFix'),
    thousands: t('thousands'),
    lengthMenu: t('length_menu'),
    loadingRecords: t('loading_records'),
    processing: t('processing'),
    search: t('search'),
    zeroRecords: t('zero_records'),
    paginate: {
      first: t('paginate.first'),
      last: t('paginate.last'),
      next: t('paginate.next'),
      previous: t('paginate.previous')
    },
    aria: {
      sortAscending: t('aria.sort_ascending'),
      sortDescending: t('aria.sort_descending')
    }
  }
};

export const setupPaginationOnDOM = () => {
  const datatablePagination = $('.pagination__holder').detach();
  datatablePagination.appendTo('.js-pagination-wrapper');
};

export const tearDownPaginationOnDOM = () => {
  $('.js-pagination-wrapper .pagination__holder').remove();
};

export const customizePageLength = (table) => {
  const { length } = table.api().state();
  const lengthAsText = length === -1 ? 'All' : length;
  $('.js-current-page-length').text(lengthAsText);
};

export const updateTableItemsCount = (table) => {
  const tableApi = table.api();
  const { recordsTotal, recordsDisplay } = tableApi.page.info();
  const $totalItems = $(table).closest('.js-table-container').find('[data-total-items]');
  const { entityName } = $totalItems.data();
  const totalItemsLabel = `${recordsTotal} ${entityName}`;
  if (recordsDisplay === recordsTotal) {
    $totalItems.text(totalItemsLabel);
  } else {
    $totalItems.text(`${recordsDisplay} of ${totalItemsLabel}`);
  }
};

export const setup = (table, opts = {}) => {
  // Tables that manage their own selection/export (e.g. Track, which persists
  // selection across server-side pages and exports all matching rows) opt out
  // of the shared, current-page-scoped wiring.
  if (opts.selectAllButton !== false) {
    setupSelectAllButton(table);
  }
  setupUnselectAllButton(table);
  if (opts.selectHooks !== false) {
    setupSelectHooks(table);
  }
  setupSearchInput(table);
  setupPageLengthDropdown(table);
  if (opts.exportButtons !== false) {
    setupExportButtons(table);
  }
};
